import { useState, useEffect } from 'react';
import { axiosInstance } from '@/axiosConfig';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import DateRangePicker from '@/components/ui/DateRangePicker';
import { toast } from 'sonner';
import { TrendingUp, DollarSign, PiggyBank, BarChart } from 'lucide-react';

// --- Componente auxiliar para as linhas do DRE (Inalterado) ---
const DreRow = ({ description, value, isTitle = false, isSub = false, isTotal = false, isNegative = false, level = 0 }) => {
  const valueColor = isNegative ? 'text-red-600' : (isTotal ? 'text-blue-700' : 'text-gray-900');
  const valueSign = isNegative ? '(-)' : (isTotal ? '(=)' : '');
  const fontWeight = isTitle || isTotal ? 'font-semibold' : 'font-medium';
  const paddingLeft = `${level * 1.5}rem`; 

  return (
    <div className={`flex items-center justify-between p-3 ${isTitle ? 'bg-gray-50' : 'bg-white'} border-b border-gray-100`}>
      <p className={`text-sm ${fontWeight} text-gray-700`} style={{ paddingLeft }}>
        {valueSign}{valueSign ? ' ' : ''}{description}
      </p>
      <p className={`text-sm ${fontWeight} ${valueColor}`}>
        {isNegative ? '-' : ''} R$ {Math.abs(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
      </p>
    </div>
  );
};
// --- FIM DO COMPONENTE ---

// Helper para capitalizar
const capitalize = (s) => {
  if (typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// Helper para pegar o mês atual (vigente)
const getPrimeiroUltimoDiaDoMes = () => {
  const today = new Date(); 
  const year = today.getFullYear();
  const month = today.getMonth();
  return { from: new Date(year, month, 1), to: new Date(year, month + 1, 0) };
};

export default function Faturamento() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    date: getPrimeiroUltimoDiaDoMes(), 
  });

  const loadData = async () => {
    setLoading(true);
    
    const params = {};
    if (filters.date?.from) {
        const localDate = new Date(filters.date.from);
        const startDate = new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 0, 0, 0, 0));
        params.data_inicio = startDate.toISOString();
    }
    if (filters.date?.to) {
        const localDate = new Date(filters.date.to);
        const endDate = new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 23, 59, 59, 999));
        params.data_fim = endDate.toISOString();
    }
    
    try {
      const dreRes = await axiosInstance.get('/dre', { params });
      setStats(dreRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados de faturamento');
      console.error("Erro detalhado (Faturamento):", error.response || error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(); 
  }, []); 
  
  if (loading || !stats) {
    return (
      <div>
        <div className="container mx-auto px-6 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">A carregar dados de faturamento...</p>
          </div>
        </div>
      </div>
    );
  }

  // ==============================================================
  // --- CÁLCULOS DO DRE (CORRIGIDOS) ---
  // ==============================================================
  const receita_bruta_vendas = stats.faturamento_vendas || 0;
  const faturamento_outras_receitas = stats.faturamento_outras_receitas || 0;
  const faturamento_bruto_total = stats.faturamento || 0; 
  const deducoes_repasses = stats.repasses_fornecedores || 0;
  
  // CORREÇÃO PRINCIPAL AQUI: O backend já manda a receita_liquida_agencia 
  // contendo a Margem + Outras Receitas. Não podemos somar de novo!
  const receita_liquida_agencia_total = stats.receita_liquida_agencia || 0;
  
  const despesas_por_categoria = stats.despesas_por_categoria || {};
  
  const custos_diretos_categorias = ['operacional', 'comissão']; 
  
  let total_custos_diretos = 0;
  let custos_diretos_map = {};
  let total_despesas_op = 0;
  let despesas_op_map = {};

  for (const [categoria, valor] of Object.entries(despesas_por_categoria)) {
    if (custos_diretos_categorias.includes(categoria)) { 
      total_custos_diretos += valor;
      custos_diretos_map[categoria] = (custos_diretos_map[categoria] || 0) + valor;
    } else {
      total_despesas_op += valor;
      despesas_op_map[categoria] = (despesas_op_map[categoria] || 0) + valor;
    }
  }

  const lucro_bruto_novo = receita_liquida_agencia_total - total_custos_diretos;
  const resultado_operacional = lucro_bruto_novo - total_despesas_op;
  const resultados_nao_op = 0.00;
  const impostos_sobre_lucro = 0.00; 
  const lucro_liquido_final = resultado_operacional + resultados_nao_op - impostos_sobre_lucro;
  // ==============================================================

  return (
    <div>
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2 pt-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Faturamento (DRE)</h1>
          <p className="text-gray-600">Visão geral do resultado (Regime de Competência)</p>
        </div>

        <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Filtros</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Período (Competência)</Label>
              <DateRangePicker
                startDate={filters.date.from}
                endDate={filters.date.to}
                onChange={({ startDate, endDate }) => {
                  setFilters({ 
                    ...filters, 
                    date: { 
                      from: startDate, 
                      to: endDate 
                    } 
                  });
                }}
                placeholder="Selecione o período"
                className="mt-1"
              />
            </div>

            <div className="flex items-end">
              <Button data-testid="filter-apply" onClick={loadData} className="w-full" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                Filtrar
              </Button>
            </div>
          </div>
        </div>
        
        {/* KPIs Principais */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="stat-card" data-testid="kpi-receita-bruta">
            <DollarSign className="w-8 h-8 text-green-600 mb-2" />
            <h3 className="text-sm font-medium text-gray-600">Faturamento Bruto Total</h3>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              R$ {faturamento_bruto_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="stat-card" data-testid="kpi-receita-liquida">
            <BarChart className="w-8 h-8 text-blue-600 mb-2" />
            <h3 className="text-sm font-medium text-gray-600">Receita Líquida (Margem)</h3>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              R$ {receita_liquida_agencia_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="stat-card" data-testid="kpi-lucro-bruto">
            <TrendingUp className="w-8 h-8 text-orange-600 mb-2" />
            <h3 className="text-sm font-medium text-gray-600">Lucro Bruto</h3>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              R$ {lucro_bruto_novo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="stat-card" data-testid="kpi-lucro-liquido">
            <PiggyBank className="w-8 h-8 text-green-700 mb-2" />
            <h3 className="text-sm font-medium text-gray-600">Lucro Líquido</h3>
            <p className="text-2xl font-bold text-green-700 mt-2">
              R$ {lucro_liquido_final.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>

        {/* --- DRE --- */}
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Demonstrativo de Resultado (DRE)</h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <DreRow description="1. RECEITAS" value={faturamento_bruto_total} isTitle level={0} />
            <DreRow description="1.1 Receita Bruta de Vendas" value={receita_bruta_vendas} level={1} />
            <DreRow description="1.2 Outras Receitas (Serviços, etc)" value={faturamento_outras_receitas} level={1} />
            <DreRow description="Deduções e Repasses a Fornecedores" value={deducoes_repasses} isTitle isNegative level={0} />
            <DreRow description="Receita Líquida da Agência (Margem Total)" value={receita_liquida_agencia_total} isTitle isTotal level={0} />
            
            <DreRow description="2. CUSTOS DIRETOS" value={total_custos_diretos} isTitle isNegative level={0} />
            {Object.keys(custos_diretos_map).length > 0 ? (
              Object.entries(custos_diretos_map).map(([categoria, valor]) => (
                <DreRow key={categoria} description={`2.1 Custos (${capitalize(categoria)})`} value={valor} isNegative level={1} />
              ))
            ) : (
              <DreRow description="2.1 Custos (Total)" value={0.00} isNegative level={1} />
            )}
            
            <DreRow description="LUCRO BRUTO" value={lucro_bruto_novo} isTitle isTotal level={0} />
            
            <DreRow description="3. DESPESAS OPERACIONAIS" value={total_despesas_op} isTitle isNegative level={0} />
            {Object.keys(despesas_op_map).length > 0 ? (
              Object.entries(despesas_op_map).map(([categoria, valor]) => (
                <DreRow key={categoria} description={`3.1 Despesas (${capitalize(categoria)})`} value={valor} isNegative level={1} />
              ))
            ) : (
              <DreRow description="3.1 Despesas (Total)" value={0.00} isNegative level={1} />
            )}
            
            <DreRow description="RESULTADO OPERACIONAL" value={resultado_operacional} isTitle isTotal level={0} />
            <DreRow description="4. RESULTADOS NÃO OPERACIONAIS" value={resultados_nao_op} isTitle level={0} />
            <DreRow description="5. IMPOSTOS SOBRE O LUCRO" value={impostos_sobre_lucro} isTitle isNegative level={0} />
            <DreRow description="LUCRO LÍQUIDO DO PERÍODO" value={lucro_liquido_final} isTitle isTotal level={0} />
          </div>
        </div>
        
      </div>
    </div>
  );
}